'use client';

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { ServiceFormField, ServiceFormFieldTypeEnum } from '@/lib/Apis/models';

// ── Field classification (template is fully admin-defined, so we group by type) ──

// Signature/photo are handled by the template's dedicated signatureShown /
// photoShown flags. The builder collapses them to "text" when saving, so we
// keep them out of the questionnaire by a narrow label heuristic.
const SIGN_PHOTO_RE = /signature|sign here|^photo|upload|insurance card|\bid card\b/i;
const SIGN_RE = /signature|sign here/i;
const PHOTO_RE = /^photo|upload|insurance card|\bid card\b/i;

export function isHandledSeparately(field: ServiceFormField): boolean {
  return field.type === ServiceFormFieldTypeEnum.Text && SIGN_PHOTO_RE.test(field.label || '');
}

// Custom forms carry signature/photo as collapsed "text" fields (the builder
// flattens them on save), so the booking flow finds them by label to render the
// dedicated SignaturePad / PhotoUpload controls.
export function findSignatureField(fields: ServiceFormField[]): ServiceFormField | undefined {
  return fields.find(
    (f) => f.type === ServiceFormFieldTypeEnum.Text && SIGN_RE.test(f.label || ''),
  );
}

export function findPhotoField(fields: ServiceFormField[]): ServiceFormField | undefined {
  return fields.find(
    (f) => f.type === ServiceFormFieldTypeEnum.Text && PHOTO_RE.test(f.label || ''),
  );
}

// Split the details fields into labelled sections so a long form reads as a few
// short, scannable blocks. Purely heuristic, so it stays template-driven.
const ADDRESS_RE = /address|street|\bcity\b|\bstate\b|\bzip\b|postal|country|apartment|\bapt\b|\bsuite\b|province|county/i;
const ADDITIONAL_RE = /note|comment|reason|additional|anything else|other info|how did you hear/i;

export interface DetailSection {
  key: 'personal' | 'address' | 'additional';
  title: string;
  fields: ServiceFormField[];
}

function detailSectionKey(f: ServiceFormField): DetailSection['key'] {
  if (f.type === ServiceFormFieldTypeEnum.Textarea || ADDITIONAL_RE.test(f.label || '')) return 'additional';
  if (ADDRESS_RE.test(f.label || '')) return 'address';
  return 'personal';
}

export function groupDetailSections(details: ServiceFormField[]): DetailSection[] {
  const meta: { key: DetailSection['key']; title: string }[] = [
    { key: 'personal', title: 'Personal info' },
    { key: 'address', title: 'Address' },
    { key: 'additional', title: 'Additional info' },
  ];
  return meta
    .map((m) => ({ ...m, fields: details.filter((f) => detailSectionKey(f) === m.key) }))
    .filter((s) => s.fields.length > 0);
}

// In the 2-column details grid, long fields take the full width; short ones
// (age, gender, city, state, zip, dates…) sit two-per-row. Heuristic so it stays
// fully template-driven without hardcoding specific questions.
const FULL_WIDTH_RE = /name|address|street|provider|email|notes?|reason|comment|description|vaccine/i;
export function isFullWidthField(field: ServiceFormField): boolean {
  if (field.type === ServiceFormFieldTypeEnum.Textarea) return true;
  if (field.type === ServiceFormFieldTypeEnum.Email || field.type === ServiceFormFieldTypeEnum.Phone) return true;
  return FULL_WIDTH_RE.test(field.label || '');
}

const SCREENING_TYPES = new Set<string>([
  ServiceFormFieldTypeEnum.Radio,
  ServiceFormFieldTypeEnum.Checkbox,
]);

// "Vaccine(s) to be given today" is already answered in step 1 (vaccine selection),
// so it's auto-filled and hidden from the questionnaire rather than re-asked.
const TEXTUAL_TYPES = new Set<string>([
  ServiceFormFieldTypeEnum.Text,
  ServiceFormFieldTypeEnum.Select,
  ServiceFormFieldTypeEnum.Textarea,
]);
export function isVaccinesTodayField(field: ServiceFormField): boolean {
  return TEXTUAL_TYPES.has(field.type) && /vaccin/i.test(field.label || '');
}

export interface GroupedFields {
  details: ServiceFormField[];
  screening: ServiceFormField[];
}

export function groupFields(fields: ServiceFormField[]): GroupedFields {
  const usable = (fields ?? []).filter(
    (f) => !isHandledSeparately(f) && !isVaccinesTodayField(f),
  );
  return {
    details: usable.filter((f) => !SCREENING_TYPES.has(f.type)),
    screening: usable.filter((f) => SCREENING_TYPES.has(f.type)),
  };
}

// A "yes/no" screening field is a radio with Yes/No options. Answering "yes"
// flags it for soft pharmacist review.
export function isYesNo(field: ServiceFormField): boolean {
  return (
    field.type === ServiceFormFieldTypeEnum.Radio &&
    (field.options ?? []).map((o) => o.toLowerCase()).join('|') === 'yes|no'
  );
}

export function flaggedYesNoLabels(
  fields: ServiceFormField[],
  formData: Record<string, any>,
): string[] {
  return fields
    .filter((f) => isYesNo(f) && String(formData[f.key] ?? '').toLowerCase() === 'yes')
    .map((f) => f.label);
}

// Best-effort contact extraction for the submission's top-level patient fields.
export function deriveContact(fields: ServiceFormField[], formData: Record<string, any>) {
  const find = (pred: (f: ServiceFormField) => boolean) => {
    const f = fields.find(pred);
    return f ? String(formData[f.key] ?? '').trim() : '';
  };
  const nameField = fields.find(
    (f) => f.type === ServiceFormFieldTypeEnum.Text && /\bname\b/i.test(f.label || ''),
  );
  const patientName =
    (nameField ? String(formData[nameField.key] ?? '').trim() : '') ||
    find((f) => f.type === ServiceFormFieldTypeEnum.Text);
  return {
    patientName,
    patientPhone: find((f) => f.type === ServiceFormFieldTypeEnum.Phone),
    patientEmail: find((f) => f.type === ServiceFormFieldTypeEnum.Email),
  };
}

export function missingRequired(fields: ServiceFormField[], formData: Record<string, any>): boolean {
  return fields.some((f) => {
    if (!f.required) return false;
    const v = formData[f.key];
    if (Array.isArray(v)) return v.length === 0;
    return v == null || String(v).trim() === '';
  });
}

// ── Insurance ────────────────────────────────────────────────────────────────────
// The insurance step always offers the same four routes; the admin only controls
// whether the step exists and which fields the "I have my details" route asks for.
// Answers ride in the same formData map under these canonical keys.
export type InsuranceChoice = 'details' | 'photo' | 'bring' | 'none';

export const INSURANCE_CHOICE_KEY = 'insuranceOption';
export const INSURANCE_PHOTOS_KEY = 'insurancePhotos';

export const INSURANCE_CHOICES: { value: InsuranceChoice; label: string; hint: string }[] = [
  { value: 'details', label: 'I have my insurance details', hint: 'Type the codes from your card' },
  { value: 'photo',   label: 'Upload my insurance card',    hint: 'Add a photo of the front and back' },
  { value: 'bring',   label: 'I’ll bring my card with me',  hint: 'We’ll scan it when you arrive' },
  { value: 'none',    label: 'I don’t have insurance',      hint: 'You can still book your visit' },
];

export function insuranceChoiceLabel(value: unknown): string {
  return INSURANCE_CHOICES.find((c) => c.value === value)?.label ?? '';
}

// ── Renderer ─────────────────────────────────────────────────────────────────────

interface ConsentFieldProps {
  field: ServiceFormField;
  value: any;
  onChange: (value: any) => void;
  invalid?: boolean;
}

const inputBase =
  'w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-hprimary-500 focus:ring-2 focus:ring-hprimary-500/20';

export function ConsentField({ field, value, onChange, invalid }: ConsentFieldProps) {
  const [touched, setTouched] = useState(false);
  const borderClass = invalid ? 'border-red-300' : 'border-gray-200';
  const T = ServiceFormFieldTypeEnum;

  const label = (
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
      {field.label}
      {field.required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
  );

  // Yes / No
  if (isYesNo(field)) {
    return (
      <div>
        {label}
        <div className="flex gap-2">
          {['Yes', 'No'].map((opt) => {
            const selected = String(value ?? '').toLowerCase() === opt.toLowerCase();
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(opt.toLowerCase())}
                className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all
                  ${selected
                    ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-hsecondary-400'}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Multi-select checkbox group
  if (field.type === T.Checkbox) {
    const arr: string[] = Array.isArray(value) ? value : [];
    const toggle = (opt: string) =>
      onChange(arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt]);
    return (
      <div>
        {label}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(field.options ?? []).map((opt) => {
            const checked = arr.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-all
                  ${checked ? 'border-hprimary-500 bg-hprimary-50 text-gray-900' : 'border-gray-200 bg-white text-gray-600 hover:border-hprimary-300'}`}
              >
                <span className={`flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-md border transition-colors ${checked ? 'border-hprimary-500 bg-hprimary-500 text-white' : 'border-gray-300 bg-white'}`}>
                  {checked && <Check className="h-3 w-3" />}
                </span>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Single-select dropdown / radio (non Yes-No)
  if (field.type === T.Select || field.type === T.Radio) {
    return (
      <div>
        {label}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputBase} ${borderClass} appearance-none`}
        >
          <option value="" disabled>Select…</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === T.Textarea) {
    return (
      <div>
        {label}
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={`${inputBase} ${borderClass} resize-none`}
        />
      </div>
    );
  }

  // Zip / numeric fields arrive as plain "text" from the template, so detect them
  // by label to apply the right keyboard + character restrictions.
  const labelL = (field.label || '').toLowerCase();
  const isZip = field.type === T.Text && /\bzip\b|postal|post ?code/.test(labelL);
  const isNumericText = field.type === T.Text && /\bage\b|\bquantity\b|how many|number of/.test(labelL);

  const htmlType =
    field.type === T.Email ? 'email'
    : field.type === T.Phone ? 'tel'
    : field.type === T.Date ? 'date'
    : field.type === T.Time ? 'time'
    : 'text';

  const inputMode: 'tel' | 'email' | 'numeric' | undefined =
    field.type === T.Phone ? 'tel'
    : field.type === T.Email ? 'email'
    : isZip || isNumericText ? 'numeric'
    : undefined;

  const autoComplete =
    field.type === T.Phone ? 'tel'
    : field.type === T.Email ? 'email'
    : isZip ? 'postal-code'
    : /\bname\b/.test(labelL) ? 'name'
    : undefined;

  const maxLength = field.type === T.Phone ? 20 : isZip ? 10 : isNumericText ? 3 : undefined;

  // Block invalid characters as they're typed.
  const sanitize = (raw: string): string => {
    if (field.type === T.Phone) return raw.replace(/[^\d+\-()\s]/g, '');
    if (isZip) return raw.replace(/[^\d-]/g, '');
    if (isNumericText) return raw.replace(/\D/g, '');
    return raw;
  };

  // Format feedback once the user leaves the field (only when they've typed something).
  const strVal = String(value ?? '');
  const formatError =
    !touched || strVal.trim() === '' ? ''
    : field.type === T.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strVal) ? 'Enter a valid email address.'
    : field.type === T.Phone && strVal.replace(/\D/g, '').length < 7 ? 'Enter a valid phone number.'
    : isZip && strVal.replace(/\D/g, '').length < 5 ? 'Enter a valid ZIP code.'
    : '';

  return (
    <div>
      {label}
      <input
        type={htmlType}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value ?? ''}
        onChange={(e) => onChange(sanitize(e.target.value))}
        onBlur={() => setTouched(true)}
        placeholder={field.placeholder}
        className={`${inputBase} ${formatError ? 'border-red-300' : borderClass}`}
      />
      {formatError && <p className="mt-1.5 text-xs text-red-500">{formatError}</p>}
    </div>
  );
}
